import * as React from 'react'

import { useCategoryElements } from './useCategoryElements'

type TOptions = {
  /**
   * used as a dependency to recalculate the intersection observer settings
   */
  dependencyData: any
  /**
   * function to be called when a category is scrolled to
   */
  onCategoryScrolledTo: (categoryId: ID) => void
  /**
   * ref holding HTMLElements rendered inside a provided container
   */
  refListElements: ReturnType<typeof useCategoryElements>
  /**
   * used as a dependency to recalculate the intersection observer settings
   */
  reconnectDependency?: number
  /**
   * known height of a sticky content
   */
  stuckContentHeight: number
}

/**
 * Handles connecting & reconnecting of the category list IntersectionObserver.
 * Responsible for automatic category selection when user is scrolling through the list.
 */
export const useCategoryObserver = ({
  dependencyData,
  refListElements,
  reconnectDependency,
  onCategoryScrolledTo,
  stuckContentHeight,
}: TOptions) => {
  const refObserver = React.useRef<Nullable<IntersectionObserver>>(null)

  const viewportHeight = document.documentElement.clientHeight
  const observerRootTopOffset = (viewportHeight - stuckContentHeight) / 5 // in order to respond to the occurrence of an element in the upper fifth of the (considered) viewport
  const topObserverRootMargin = Math.ceil(stuckContentHeight + observerRootTopOffset) * -1 // Math.ceil is here to avoid unwanted automatic rounding of computed values inside of observer's rootMargin property
  const bottomObserverRootMargin = (viewportHeight + topObserverRootMargin - 1) * -1 // subtraction of one pixel serves to create just a 1px-height intersection root

  const handleIntersection: IntersectionObserverCallback = React.useCallback(
    (entries) => {
      entries.forEach((entry, index, entries) => {
        const isNoIntersection = entries.every((entry) => !entry.isIntersecting)

        // select the first category if the user scrolled to the top of the page
        // this should reset the selection to the first category after scroll-to-top action (button)
        // OR on iOS where the page gets scrolled to the top after adding an item to the cart
        // do this only once (for one entry out of all observed entries)
        if (isNoIntersection && window.scrollY === 0) {
          const firstCategoryElement = refListElements.current.at(0)
          if (!firstCategoryElement) return

          onCategoryScrolledTo(firstCategoryElement.id)
          return
        }

        // select category if it is intersecting boundaries specified by the observer options
        if (entry.isIntersecting) {
          onCategoryScrolledTo(entry.target.id)
        }
      })
    },
    [onCategoryScrolledTo, refListElements],
  )

  // set up the intersection observer for every category
  React.useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      root: null, // The element that is used as the viewport for checking visibility of the target. Must be the ancestor of the target. Defaults to the browser viewport if not specified or if null
      threshold: 0,
      rootMargin: `${topObserverRootMargin}px 0px ${bottomObserverRootMargin}px 0px`,
    })

    refObserver.current = observer

    refListElements.current.forEach((element) => observer.observe(element))

    return () => {
      observer.disconnect()
    }
  }, [
    dependencyData,
    refListElements,
    handleIntersection,
    topObserverRootMargin,
    bottomObserverRootMargin,
    reconnectDependency,
  ])

  return refObserver
}
